import {Button, Modal, Spin, Tabs, TabsProps} from "antd";
import React, {useContext, useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {FormattedMessage} from "react-intl";
import '../assets/css/Dentobook.css';
import {Appointment, Patient} from "../api-client";
import {getPatientApi, getCalendarApi} from "../api/apiInstances";
import PatientDetailsItem from "../components/patient/PatientDetailsItem";
import DetailWrapper from "../components/common/DetailWrapper";
import {CloseOutlined, DeleteOutlined, LoadingOutlined, PrinterOutlined} from "@ant-design/icons";
import {accessibilityRoles} from "../utils/accessUtils";
import AccessControl from "../components/common/AccessControl";
import PatientTreatment from "../components/patient/PatientTreatment";
import PatientPhotos from "../components/patient/PatientPhotos";
import {isMobileOnly} from "react-device-detect";
import {AlertContext} from "../context/AlertContext";
import PatientNzis from "../components/patient/PatientNzis";
import {DentobookApplicationContext} from "../context/DentobookApplicationContext";
import PatientFinancesTabs from "../components/patient/PatientFinancesTabs";
import logger from "../utils/logger";
import {ALERT_DURATIONS} from "../constants/alerts";

const PatientDetails = () => {

    const {principal, userOrganization} = useContext(DentobookApplicationContext);

    const navigate = useNavigate();

    const {id} = useParams();

    const location = useLocation();

    const {setVisible} = useContext(AlertContext);

    const [previousPath, setPreviousPath] = useState<string>('');
    const [editMode, setEditMode] = useState<boolean>(false);
    const [patient, setPatient] = useState<Patient | undefined>(undefined);
    const [activeKey, setActiveKey] = useState<string>('main-information');
    const [subKey, setSubKey] = useState<string>('');
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const spinnerIcon = <LoadingOutlined style={{fontSize: 24}} spin/>;

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const redirectedFrom = searchParams.get("redirectedFrom");
        const tab = searchParams.get("tab");
        const subTab = searchParams.get("subTab");
        if (redirectedFrom) {
            setPreviousPath(redirectedFrom);
        }
        if (tab) {
            setActiveKey(tab);
        }
        if (subTab) {
            setSubKey(subTab);
        }
    }, [location.search]);

    const items: TabsProps['items'] = [
        {
            label: <FormattedMessage id="main_information"/>,
            key: 'main-information',
            children: patient ? <PatientDetailsItem patient={patient} setPatient={setPatient} editMode={editMode}
                                          setEditMode={setEditMode}/> : null
        },
        {
            label: <FormattedMessage id="medical_history"/>,
            key: 'medical-history',
            children: patient ? <PatientTreatment selectedAppointment={selectedAppointment!}
                                        setSelectedAppointment={setSelectedAppointment} patient={patient} tab={subKey}
                                        setTab={setSubKey}/> : null
        },
    ];

    if (userOrganization?.country === 'BG' && (principal?.user?.role === 'CLINIC_ADMIN' || principal?.user?.role === 'MEDICAL_DOCTOR')) {
        items.splice(3, 0,
            {
                label: <FormattedMessage id="nzis"/>,
                key: 'nzis',
                children: patient ? <PatientNzis patient={patient}/> : null
            })
    }

    if (principal?.features.includes('FINANCES')) {
        items.splice(2, 0, {
            label: <FormattedMessage id="finances"/>,
            key: 'finances',
            children: patient ? <PatientFinancesTabs patient={patient}/> : null
        });
    }

    if (principal?.features.includes('PHOTO_UPLOAD')) {
        items.push({
            label: <FormattedMessage id="photos"/>,
            key: 'photos',
            children: patient ? <PatientPhotos patient={patient}/> : null
        });
    }

    const patientApi = getPatientApi();
    const calendarApi = getCalendarApi();

    useEffect(() => {
        if (id == null) {
            return;
        }

        patientApi.fetchPatientById(id)
            .then((response) => {
                setPatient(response.data);
            })
            .catch(err => {
                logger.log(err);
            });
    }, []);

    const generatePdf = async () => {
        if (!selectedAppointment?.id || !patient) return;

        setLoading(true);
        try {
            // The OpenAPI spec types application/pdf with an empty schema, so the
            // generated client types `data` as void. At runtime, requesting a blob
            // returns the PDF bytes — cast to access them.
            const pdfResponse = await patientApi.generatePdfForPatient(
                selectedAppointment.id,
                { responseType: 'blob' }
            );
            const blob = new Blob([pdfResponse.data as unknown as BlobPart], {type: 'application/pdf'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${patient.firstName}_${patient.lastName}.pdf`;
            link.click();
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch (error) {
            logger.error(error);
        } finally {
            setLoading(false);
        }
    }

    const handleOk = () => {
        if (!patient?.id) return;

        patientApi.deletePatient(patient.id)
            .then(() => {
                setIsModalOpen(false);
                setVisible(true);
                setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
                navigate('/patients');
                if (patient.id != null) {
                    calendarApi.deletePatientCalendarEvents(patient.id);
                }
            })
            .catch(err => logger.log(err));
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const footer = <div className="modal-btn-wrapper">
        <Button className="modal-btn" onClick={handleCancel}>
            {<FormattedMessage id="cancel"/>}
        </Button>
        <Button className="modal-btn" type="primary" onClick={handleOk}
        >
            {<FormattedMessage id="confirm"/>}
        </Button>
    </div>;

    if (!patient?.id) {
        return null;
    }

    return (
        <AccessControl notAccessibleFor={[accessibilityRoles.PLATFORM_ADMINISTRATOR, accessibilityRoles.PATIENT]}>
            <Modal
                transitionName=""
                footer={footer}
                className="dentobook-modal"
                title={<FormattedMessage id="delete_patient"/>}
                closeIcon={<CloseOutlined className={'icon-24px'} onClick={handleCancel}/>}
                open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
                <h3 className={"dentobook-content font-weight-500 font-24 text-align-center " + (isMobileOnly ? "mt-112" : "")}>
                    <FormattedMessage id="confirmation_text"/>
                </h3>
            </Modal>
            <Spin indicator={spinnerIcon} spinning={loading}
                  style={{position: "fixed", top: "50%", left: "50%", zIndex: "99999"}}/>
            <DetailWrapper navigateBack={previousPath ? ('/' + previousPath) : "/patients"}>
                <div className="top-right-btns" style={{marginTop: -64}}>
                    <AccessControl accessibleFor={[accessibilityRoles.CLINIC_ADMIN]}>
                        <Button className="text-black font-20" icon={<DeleteOutlined style={{fontSize: '140%'}}/>}
                                size={"large"} type="link" onClick={() => setIsModalOpen(true)}></Button>
                    </AccessControl>
                    {' '}
                    <Button className="text-black font-20 ml-2" icon={<PrinterOutlined style={{fontSize: '140%'}}/>}
                            size={"large"}
                            disabled={activeKey !== 'medical-history' || !selectedAppointment?.id}
                            type="link" onClick={() => {
                        generatePdf()
                    }}></Button>
                </div>
                <h3>{patient.firstName + ' ' + patient.lastName}</h3>
                <Tabs activeKey={activeKey} onChange={key => setActiveKey(key)}
                      destroyInactiveTabPane={true} defaultActiveKey="main-information" items={items}/>
            </DetailWrapper>
        </AccessControl>
    );
};

export default PatientDetails;